import * as React from 'react';
import { Icon } from './Icon';

// Layout primitives — mirror Docs/Design Handoff/portal/common.jsx. Card, table
// parts, page header (with origin-aware breadcrumb), section title, button. Used
// across every persona screen so the prototype is recreated 1:1.

// ── Card ────────────────────────────────────────────────────────────────
export interface CardProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  pad?: string | number;
  hover?: boolean;
  onClick?: () => void;
}
export function Card({ children, style, pad, hover, onClick }: CardProps) {
  const [h, setH] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hover && setH(true)}
      onMouseLeave={() => hover && setH(false)}
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
        boxShadow: h ? 'var(--shadow-md)' : 'var(--shadow-card)',
        transition: 'box-shadow .15s, transform .15s', transform: h ? 'translateY(-1px)' : 'none',
        cursor: onClick ? 'pointer' : 'default', padding: pad, ...style,
      }}
    >
      {children}
    </div>
  );
}

export interface CardHeadProps {
  title: React.ReactNode;
  right?: React.ReactNode;
  sub?: React.ReactNode;
}
export function CardHead({ title, right, sub }: CardHeadProps) {
  return (
    <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

// ── Table primitives ────────────────────────────────────────────────────
export interface Col {
  label: string;
  align?: 'left' | 'right' | 'center';
  w?: number | string;
}
export function Table({ cols, children }: { cols: Col[]; children: React.ReactNode }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {cols.map((c, i) => (
            <th
              key={i}
              style={{
                textAlign: c.align || 'left', padding: '10px 16px', fontSize: 11, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)',
                background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', width: c.w,
              }}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

export function Td({ children, align, style, colSpan, onClick }: { children?: React.ReactNode; align?: 'left' | 'right' | 'center'; style?: React.CSSProperties; colSpan?: number; onClick?: (e: React.MouseEvent) => void }) {
  return (
    <td colSpan={colSpan} onClick={onClick} style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, color: 'var(--text-primary)', textAlign: align, verticalAlign: 'middle', ...style }}>
      {children}
    </td>
  );
}

export function Row({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  const [h, setH] = React.useState(false);
  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{ cursor: onClick ? 'pointer' : 'default', background: h && onClick ? 'var(--bg-secondary)' : 'transparent' }}
    >
      {children}
    </tr>
  );
}

export function Mono({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <span style={{ fontFamily: 'var(--mono)', fontSize: 12.5, ...style }}>{children}</span>;
}

// ── Page header (origin-aware breadcrumb) ────────────────────────────────
export interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  back?: boolean;
  onBack?: () => void;
  breadcrumb?: string[];
  onCrumb?: (index: number) => void;
}
export function PageHeader({ title, subtitle, actions, back, onBack, breadcrumb, onCrumb }: PageHeaderProps) {
  return (
    <div style={{ marginBottom: 22 }}>
      {breadcrumb && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          {breadcrumb.map((c, i) => {
            const isLast = i === breadcrumb.length - 1;
            const clickable = !isLast && !!onCrumb;
            return (
              <React.Fragment key={i}>
                {i > 0 && <Icon name="chevron-right" size={13} color="var(--border-strong)" />}
                <span
                  onClick={clickable ? () => onCrumb!(i) : undefined}
                  style={{ color: isLast ? 'var(--text-primary)' : clickable ? 'var(--accent)' : 'var(--text-secondary)', fontWeight: isLast ? 500 : 400, cursor: clickable ? 'pointer' : 'default' }}
                >
                  {c}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {back && (
            <button
              type="button"
              onClick={onBack}
              style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}
            >
              <Icon name="arrow-left" size={16} color="var(--text-secondary)" />
            </button>
          )}
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.01em' }}>{title}</h1>
            {subtitle && <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>{subtitle}</p>}
          </div>
        </div>
        {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
      </div>
    </div>
  );
}

export function SectionTitle({ children, accent, right }: { children: React.ReactNode; accent?: string; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 0 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {accent && <span style={{ width: 3, height: 15, borderRadius: 2, background: accent }} />}
        <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-.01em' }}>{children}</span>
      </div>
      {right}
    </div>
  );
}

// ── Button ───────────────────────────────────────────────────────────────
export interface BtnProps {
  kind?: 'primary' | 'secondary' | 'ghost';
  icon?: string;
  children?: React.ReactNode;
  sm?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  accent?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
}
export function Btn({ kind = 'secondary', icon, children, sm, onClick, accent, style, disabled, type = 'button', title }: BtnProps) {
  const [h, setH] = React.useState(false);
  const variants: Record<string, React.CSSProperties> = {
    primary: { background: accent || 'var(--accent)', color: '#fff', border: `1px solid ${accent || 'var(--accent)'}` },
    secondary: { background: 'var(--surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' },
    ghost: { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid transparent' },
  };
  return (
    <button
      type={type}
      title={title}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: sm ? '5px 11px' : '8px 14px',
        borderRadius: 8, fontSize: sm ? 12 : 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all .15s',
        filter: h && !disabled ? 'brightness(0.97)' : 'none', opacity: disabled ? 0.5 : 1,
        ...variants[kind], ...style,
      }}
    >
      {icon && <Icon name={icon} size={sm ? 13 : 14} />}
      {children}
    </button>
  );
}
